/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
 the European Commission - subsequent versions of the EUPL (the "Licence");
 You may not use this work except in compliance with the Licence.
 You may obtain a copy of the Licence at:

 https://joinup.ec.europa.eu/software/page/eupl

 Unless required by applicable law or agreed to in writing, software
 distributed under the Licence is distributed on an "AS IS" basis,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the Licence for the specific language governing permissions and
 limitations under the Licence. */

import { ApolloProvider } from "@apollo/client/react";
import { ComponentToggleProvider } from "@entur/react-component-toggle";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { AuthProvider } from "./auth/auth";
import { useActiveUiMode } from "./components/UiModeSwitch";
import { ConfigContext } from "./config/ConfigContext";
import { fetchConfig } from "./config/fetchConfig";
import LegacyApp from "./containers/LegacyApp";
import ModernApp from "./containers/modern/App";
import { getTiamatClient } from "./graphql/clients";
import { store } from "./store/store";

/**
 * AppRouter - Switches between Legacy and Modern App.
 * The config's uiMode field is the authority:
 *   "legacy" (default) — always renders LegacyApp
 *   "modern"           — always renders ModernApp
 *   "dual"             — user can switch; Redux uiMode remembers their choice,
 *                        dualUiConfig.defaultUiMode applies until they make one
 */
const AppRouter = () => {
  const activeUiMode = useActiveUiMode();

  return activeUiMode === "modern" ? <ModernApp /> : <LegacyApp />;
};

const AuthenticatedApp = () => {
  const client = getTiamatClient();

  return (
    <Provider store={store}>
      <ApolloProvider client={client}>
        <AppRouter />
      </ApolloProvider>
    </Provider>
  );
};

function renderIndex(config) {
  const root = createRoot(document.getElementById("root"));
  root.render(
    <ComponentToggleProvider
      maxFeatureDepth={2}
      flags={config.featureFlags}
      importFn={(featurePathComponents) => {
        if (featurePathComponents.length === 1) {
          return import(`./ext/${featurePathComponents[0]}/index.ts`);
        } else if (featurePathComponents.length === 2) {
          return import(
            `./ext/${featurePathComponents[0]}/${featurePathComponents[1]}/index.ts`
          );
        } else {
          throw new Error("Max feature depth is 2");
        }
      }}
    >
      <ConfigContext.Provider value={config}>
        <AuthProvider>
          <AuthenticatedApp />
        </AuthProvider>
      </ConfigContext.Provider>
    </ComponentToggleProvider>,
  );
}

fetchConfig().then((config) => {
  window.config = config;
  renderIndex(config);
});
